// components/common/charts/VerticalStackedBarChart.jsx - Vertical stacked bars component
import React, { useState, useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  CHART_COLORS,
  formatCompactNumber,
  MobileLegend,
  CustomChartDot,
  getResponsiveChartConfig,
  DesktopLegend,
} from "../../utils/chartUtils";

/**
 * VerticalStackedBarChart Component
 * A reusable vertical stacked bar chart with optional comparison line
 * 
 * @param {Array} data - Array of data points with structure:
 *   [{ label: "Jan 2025", segment1: 1000, segment2: 500, segment3: 300, comparison: 1500 }, ...]
 * 
 * @param {Object} config - Chart configuration:
 *   - segments: Array of segment configs [{ dataKey, name, color }]
 *   - comparisonKey: string - Optional key for comparison line
 *   - comparisonColor: string - Comparison line color
 *   - showComparison: boolean - Show comparison line (default: false)
 *   - showGrowth: boolean - Show YoY growth in tooltip (default: false)
 *   - valueFormatter: function - Custom value formatter
 *   - comparisonLabel: string - Label for comparison
 *   - labelKey: string - Key for x-axis labels (default: "label")
 * 
 * @param {String} title - Optional chart title
 * @param {Boolean} showLegend - Show legend (default: true)
 */
export const VerticalStackedBarChart = ({
  data,
  config = {},
  title = null,
  showLegend = true,
}) => {
  const [showMobileLegend, setShowMobileLegend] = useState(false);

  // Default config values
  const {
    segments = [],
    comparisonKey = null,
    comparisonColor = CHART_COLORS.lastYear,
    showComparison = false,
    showGrowth = false,
    valueFormatter = (v) => `$${formatCompactNumber(v)}`,
    comparisonLabel = "Last Year",
    labelKey = "label",
    comparisonName = "Last Year Total",
  } = config;

  // Validate data
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    return data;
  }, [data]);

  // Get responsive config
  const responsiveConfig = getResponsiveChartConfig();

  // Build legend items
  const legendItems = useMemo(() => {
    const items = segments.map(segment => ({
      color: segment.color,
      label: segment.name,
      type: "bar"
    }));
    
    if (showComparison && comparisonKey) {
      items.push({ 
        color: comparisonColor, 
        label: comparisonName, 
        type: "line" 
      });
    }
    
    return items;
  }, [segments, comparisonColor, comparisonName, showComparison, comparisonKey]);

  // Custom tooltip for stacked bars
  const CustomStackedTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    // Separate stacked bars from comparison line
    const stackedItems = payload.filter(p => 
      !comparisonKey || p.dataKey !== comparisonKey
    );
    const comparisonItem = comparisonKey 
      ? payload.find(p => p.dataKey === comparisonKey)
      : null;

    // Calculate total
    const currentTotal = stackedItems.reduce((sum, item) => sum + (item.value || 0), 0);

    // Don't show if no data
    if (currentTotal === 0 && (!comparisonItem || comparisonItem.value === 0)) {
      return null;
    }

    return (
      <div
        style={{
          padding: "12px",
          background: "white",
          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
          borderRadius: "5px",
          fontSize: "14px",
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "8px" }}>{label}</div>

        {/* Stacked segments breakdown */}
        {currentTotal > 0 && (
          <div style={{ marginBottom: "8px" }}>
            {stackedItems.map((item, index) => (
              item.value > 0 && (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "16px",
                    marginBottom: "4px",
                  }}
                >
                  <span style={{ color: item.color, fontWeight: "500" }}>
                    {item.name}:
                  </span>
                  <span style={{ fontWeight: "600" }}>
                    {valueFormatter(item.value)}
                  </span>
                </div>
              )
            ))}
          </div>
        )}

        {/* Total */}
        {currentTotal > 0 && stackedItems.length > 1 && (
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              paddingTop: "6px",
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              fontWeight: "bold",
            }}
          >
            <span>Total:</span>
            <span>{valueFormatter(currentTotal)}</span>
          </div>
        )}

        {/* Comparison section */}
        {comparisonItem && comparisonItem.value > 0 && (
          <div
            style={{
              borderTop: currentTotal > 0 ? "1px solid #e5e7eb" : "none",
              marginTop: currentTotal > 0 ? "8px" : "0",
              paddingTop: currentTotal > 0 ? "6px" : "0",
              fontSize: "13px",
              color: CHART_COLORS.axisText,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "16px",
                marginBottom: "4px",
              }}
            >
              <span>{comparisonLabel}:</span>
              <span style={{ fontWeight: "500" }}>
                {valueFormatter(comparisonItem.value)}
              </span>
            </div>
            {showGrowth && currentTotal > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "16px",
                }}
              >
                <span>YoY Growth:</span>
                <span
                  style={{
                    fontWeight: "600",
                    color:
                      currentTotal > comparisonItem.value 
                        ? CHART_COLORS.growthPositive 
                        : CHART_COLORS.growthNegative,
                  }}
                >
                  {(
                    ((currentTotal - comparisonItem.value) / comparisonItem.value) *
                    100
                  ).toFixed(1)}
                  %
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Empty state
  if (!chartData || chartData.length === 0) {
    return (
      <div className="h-64 sm:h-96 flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <div className="text-4xl mb-2 text-gray-400">📊</div>
          <div className="text-gray-500 font-medium text-sm sm:text-base">
            No data available
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* Optional Title */}
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}

      {/* Mobile Legend */}
      {showLegend && (
        <MobileLegend 
          legendItems={legendItems}
          showLegend={showMobileLegend} 
          onToggle={() => setShowMobileLegend(!showMobileLegend)} 
        />
      )}

      {/* Chart Container */}
      <div className="w-full h-80 sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={responsiveConfig.margin}
            barCategoryGap={responsiveConfig.barCategoryGap}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={CHART_COLORS.gridLines}
              vertical={false}
            />

            {/* X-AXIS */}
            <XAxis
              dataKey={labelKey}
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: responsiveConfig.xAxisConfig.fontSize,
                fill: CHART_COLORS.axisText,
              }}
              angle={responsiveConfig.xAxisConfig.angle}
              textAnchor={responsiveConfig.xAxisConfig.textAnchor}
              height={responsiveConfig.xAxisConfig.height}
            />

            {/* Y-AXIS */}
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: responsiveConfig.yAxisConfig.fontSize,
                fill: CHART_COLORS.axisText,
              }}
              tickFormatter={(value) => formatCompactNumber(Math.round(value))}
              domain={[0, "auto"]}
              width={responsiveConfig.yAxisConfig.width}
            />

            {/* TOOLTIP */}
            <Tooltip
              content={<CustomStackedTooltip />}
              cursor={{ fill: "rgba(4, 32, 43, 0.1)" }}
            />

            {/* DESKTOP LEGEND */}
            {showLegend && (
              <Legend
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{
                  paddingTop: "20px",
                  fontSize: "14px",
                  display: typeof window !== 'undefined' && window.innerWidth < 640 ? 'none' : 'block',
                }}
                content={() => <DesktopLegend legendItems={legendItems} />}
              />
            )}

            {/* STACKED BARS - Order matters: Bottom to Top */}
            {segments.map((segment, index) => (
              <Bar
                key={segment.dataKey}
                dataKey={segment.dataKey}
                fill={segment.color}
                name={segment.name}
                stackId="stack"
                radius={index === segments.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} // Round top bar only
                maxBarSize={50}
              />
            ))}

            {/* COMPARISON LINE (Optional) */}
            {showComparison && comparisonKey && (
              <Line
                type="monotone"
                dataKey={comparisonKey}
                stroke={comparisonColor}
                strokeWidth={2}
                name={comparisonName}
                dot={<CustomChartDot dataKey={comparisonKey} />}
                connectNulls={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default VerticalStackedBarChart;